import {
  IBenefitDetails,
  IBenefitsUsageResponse,
  IBenefitUsageDetail,
  IInsuranceBenefitCoverages,
  ISearchOptions
} from '../../../interfaces/benefits';
import {
  IChangeBusinessData,
  IChangeBusinessDataResponse,
  ICompanyPlanBenefitsResponse,
  ICustomResponse,
  IExternalFileResponse,
  IPaymentUrlResponse
} from '../../../interfaces/businessData';
import { IRankingCardData } from '../../../interfaces/cards';
import {
  ICompanyBannerResponse,
  IFlexibleRemunerationWallet,
  IDomainResponse,
  ICompanyReportsResponse,
  IFlexibleRemunerationPayRollCycles,
  IFlexibleRemunerationConsumptions,
  IFlexibleRemunerationRequests
} from '../../../interfaces/company';
import { ICompanyPlanResponse, IPlan } from '../../../interfaces/plans';
import { IHealthReport, IHealthReportListResponse } from '../../../interfaces/reports';
import { IUpdateFlexibleRemunerationRequestBody } from '../../../interfaces/requests';
import {
  BENEFITS_BY_PLAN_MOCK,
  BENEFITS_USAGE_MOCK,
  BENEFIT_USAGE_MOCK,
  INSURANCE_BENEFIT_COVERAGES_MOCK
} from '../../../mocks/benefits';
import {
  CHANGE_COMPANY_DATA_MOCK_RESPONSE,
  COMPANY_PAYMENT_URL_MOCK,
  PLAN_MOCK_RESPONSE_NE,
  COMPANY_DOMAINS_MOCK,
  MOCK_COMPANY_SELECTED_BENEFITS,
  BANNER_MOCK,
  MOCK_COMPANY_PLANS,
  MOCK_BENEFIT_DETAIL,
  MOCK_COMPANY_FLEXIBLE_REMUNERATION_WALLET,
  MOCK_COMPANY_REPORTS_RESPONSE,
  MOCK_COMPANY_FLEXIBLE_REMUNERATION_PAYROLL_CYCLE,
  MOCK_COMPANY_FLEXIBLE_REMUNERATION_CONSUMPTIONS,
  MOCK_DOWNLOAD_REPORT_RESPONSE,
  MOCK_HEALTH_REPORT_DATA,
  MOCK_HEALTH_REPORT_LIST,
  MOCK_COMPANY_FLEXIBLE_REMUNERATION_REQUESTS
} from '../../../mocks/company';
import { DEFAULT_CUSTOM_RESPONSE_MOCK } from '../../../mocks/customResponse';
import { HttpService, HttpServiceServer } from '../../http/httpService';

export class Company {
  static async getPaymentUrl(companyId: string): Promise<IPaymentUrlResponse> {
    const response = await HttpService.get<IPaymentUrlResponse>(
      `companies/${companyId}/external-payment`,
      {},
      COMPANY_PAYMENT_URL_MOCK
    );
    return response.data;
  }

  static async getBenefitsUsage(companyId: string, searchOptions: ISearchOptions): Promise<IBenefitsUsageResponse> {
    const response = await HttpService.get<IBenefitsUsageResponse>(
      `companies/${companyId}/benefits`,
      {
        params: {
          startDate: searchOptions.dateRange[0],
          endDate: searchOptions.dateRange[1],
          period: searchOptions.period
        }
      },
      { data: BENEFITS_USAGE_MOCK }
    );
    return response.data;
  }

  static async getBenefitUsage(
    companyId: string,
    providerId: number,
    benefitId: number,
    searchOptions: ISearchOptions
  ): Promise<IBenefitUsageDetail> {
    const response = await HttpService.get<IBenefitUsageDetail>(
      `companies/${companyId}/providers/${providerId}/benefits/${benefitId}`,
      {
        params: {
          startDate: searchOptions.dateRange[0],
          endDate: searchOptions.dateRange[1],
          period: searchOptions.period
        }
      },
      { data: BENEFIT_USAGE_MOCK }
    );
    return response.data;
  }

  static async makeProductRequest(companyId: string, product: string): Promise<void> {
    const body = {
      product
    };

    await HttpService.post<ICustomResponse>(`request/${companyId}/product`, body, {}, DEFAULT_CUSTOM_RESPONSE_MOCK);
  }

  static async patchCompanyAttributes(
    companyId: string,
    companyData: IChangeBusinessData
  ): Promise<IChangeBusinessDataResponse> {
    const response = await HttpService.patch<IChangeBusinessDataResponse>(
      `companies/${companyId}`,
      companyData,
      {},
      CHANGE_COMPANY_DATA_MOCK_RESPONSE
    );
    return response.data;
  }

  static async getPlans(companyId: string, lang?: string): Promise<IPlan[]> {
    lang = lang || 'es';
    const response = await HttpService.get<IPlan[]>(
      `companies/${companyId}/plans`,
      { params: { language: lang } },
      { data: PLAN_MOCK_RESPONSE_NE }
    );
    return response.data;
  }

  static async integrateSlack(companyId: string, code: string): Promise<void> {
    const body = { code, notify: true };
    await HttpService.post<ICustomResponse>(
      `companies/${companyId}/slack-integration`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }

  static async getDomains(companyId: string): Promise<IDomainResponse> {
    const response = await HttpService.get<IDomainResponse>(
      `companies/${companyId}/get-domains`,
      {},
      { data: COMPANY_DOMAINS_MOCK }
    );
    return response.data;
  }

  static async getSelectedBenefits(companyId: string): Promise<IRankingCardData> {
    const response = await HttpServiceServer.get<IRankingCardData>(
      `companies/${companyId}/selected-benefits`,
      {},
      { data: MOCK_COMPANY_SELECTED_BENEFITS }
    );
    return response.data;
  }

  static async getCompanyBanners(companyId: string, productTypes: string[]): Promise<ICompanyBannerResponse> {
    const response = await HttpServiceServer.get<ICompanyBannerResponse>(
      `companies/${companyId}/banners`,
      { params: { productTypes: productTypes.join(',') } },
      { data: BANNER_MOCK }
    );
    return response.data;
  }

  static async getCompanyPlans(companyId: string, locale: string): Promise<ICompanyPlanResponse> {
    const response = await HttpServiceServer.get<ICompanyPlanResponse>(
      `companies/${companyId}/plans`,
      {
        params: { locale: locale }
      },
      { data: MOCK_COMPANY_PLANS }
    );

    return response.data;
  }

  static async getCompanyBenefitDetail(companyId: string, benefitId: string): Promise<IBenefitDetails> {
    const response = await HttpServiceServer.get<IBenefitDetails>(
      `companies/${companyId}/benefits/${benefitId}/details`,
      {},
      { data: MOCK_BENEFIT_DETAIL }
    );
    return response.data;
  }

  static async sendSalesMessage(companyId: string, messageType = ''): Promise<void> {
    await HttpServiceServer.post<IPlan>(
      `companies/${companyId}/sales-message/${messageType}`,
      {},
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }

  static async getPlanBenefits(companyId: string, planId: number): Promise<ICompanyPlanBenefitsResponse> {
    const response = await HttpServiceServer.get<ICompanyPlanBenefitsResponse>(
      `companies/${companyId}/plan/${planId}/benefits`,
      {},
      { data: BENEFITS_BY_PLAN_MOCK }
    );
    return response.data;
  }

  static async getCompanyInsuranceCoverages(
    companyId: string,
    benefitId: string,
    planId: string,
    locale: string
  ): Promise<IInsuranceBenefitCoverages> {
    const response = await HttpServiceServer.get<IInsuranceBenefitCoverages>(
      `companies/${companyId}/benefits/${benefitId}/coverages?locale=${locale}&planId=${planId}`,
      {},
      { data: INSURANCE_BENEFIT_COVERAGES_MOCK }
    );
    return response.data;
  }

  static async notifyAddFunds(companyId: string, amount: number, currency: string, proForma: boolean): Promise<void> {
    const body = {
      amount,
      currency,
      proForma
    };
    await HttpServiceServer.post<IPlan>(
      `/companies/${companyId}/flexible-remuneration/wallet/add-funds`,
      body,
      {},
      DEFAULT_CUSTOM_RESPONSE_MOCK
    );
  }

  static async getCompanyFlexibleRemunerationWallet(companyId: string): Promise<IFlexibleRemunerationWallet> {
    const response = await HttpServiceServer.get<IFlexibleRemunerationWallet>(
      `/companies/${companyId}/flexible-remuneration/wallet`,
      {},
      { data: MOCK_COMPANY_FLEXIBLE_REMUNERATION_WALLET }
    );
    return response.data;
  }

  static async getMonthlyReports(companyId: string): Promise<ICompanyReportsResponse> {
    const response = await HttpServiceServer.get<ICompanyReportsResponse>(
      `/companies/${companyId}/monthly-reports`,
      {},
      { data: MOCK_COMPANY_REPORTS_RESPONSE }
    );
    return response.data;
  }

  static async getCompanyFlexibleRemunerationPayRollCycles(
    companyId: string
  ): Promise<IFlexibleRemunerationPayRollCycles> {
    const response = await HttpServiceServer.get<IFlexibleRemunerationPayRollCycles>(
      `/companies/${companyId}/flexible-remuneration/payroll-cycles`,
      {},
      { data: MOCK_COMPANY_FLEXIBLE_REMUNERATION_PAYROLL_CYCLE }
    );
    return response.data;
  }

  static async getCompanyFlexibleRemunerationConsumptions(
    companyId: string,
    cycleId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<IFlexibleRemunerationConsumptions> {
    const params = new URLSearchParams();

    if (cycleId) {
      params.set('payrollId', cycleId);
    }
    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }

    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await HttpServiceServer.get<IFlexibleRemunerationConsumptions>(
      `/companies/${companyId}/flexible-remuneration/consumptions${queryString}`,
      {},
      { data: MOCK_COMPANY_FLEXIBLE_REMUNERATION_CONSUMPTIONS }
    );
    return response.data;
  }

  static async getCompanyPayRollReports(companyId: string, payrollId: string): Promise<IExternalFileResponse> {
    const response = await HttpServiceServer.get<IExternalFileResponse>(
      `/companies/${companyId}/flexible-remuneration/payroll-cycles/${payrollId}/report`,
      {},
      { data: MOCK_DOWNLOAD_REPORT_RESPONSE }
    );
    return response.data;
  }

  static async getCompanyHealthReports(companyId: string): Promise<IHealthReportListResponse> {
    const response = await HttpServiceServer.get<IHealthReportListResponse>(
      `/companies/${companyId}/health-reports/`,
      {},
      { data: MOCK_HEALTH_REPORT_LIST }
    );
    return response.data;
  }

  static async getCompanyFlexibleRemunerationRequests(
    companyId: string,
    status?: string
  ): Promise<IFlexibleRemunerationRequests> {
    const params = status ? `?status=${status}` : '';
    const response = await HttpServiceServer.get<IFlexibleRemunerationRequests>(
      `/companies/${companyId}/flexible-remuneration/requests${params}`,
      {},
      { data: MOCK_COMPANY_FLEXIBLE_REMUNERATION_REQUESTS }
    );
    return response.data;
  }

  static async getCompanyHealthReportData(companyId: string, periodDate: string): Promise<IHealthReport> {
    const response = await HttpServiceServer.get<IHealthReport>(
      `/companies/${companyId}/health-report?date=${periodDate}`,
      {},
      { data: MOCK_HEALTH_REPORT_DATA }
    );
    return response.data;
  }

  static async updateCompanyFlexibleRemunerationRequest(
    companyId: string,
    requestId: string,
    body: IUpdateFlexibleRemunerationRequestBody
  ): Promise<void> {
    await HttpServiceServer.patch<void>(
      `/companies/${companyId}/flexible-remuneration/requests/${requestId}`,
      body,
      {},
      {}
    );
  }
}

export default Company;
